import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  RequestOptionState,
  RequestOptionTemplateState,
  createRequestOptionTemplate,
  deleteRequestOptionTemplate,
  getRequestOptionTemplates,
  getRequestOptions,
  updateRequestOptionTemplate,
} from "../../slices/requests";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import {
  CheckIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import DisabledTextInput from "../forms/DisabledTextInput";
import DangerZone from "../forms/DangerZone";

export default function RoomsTemplates() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getRequestOptions());
    dispatch(getRooms());
    dispatch(getRequestOptionTemplates());
  }, [dispatch]);

  // Set state
  const [selectedTemplate, setSelectedTemplate] =
    useState<RequestOptionTemplateState | null>(null);
  const [createTemplateForm, setCreateTemplateForm] = useState<{
    name: string;
    template: RequestOptionState[];
  }>({
    name: "",
    template: [],
  });
  const [editTemplateRooms, setEditTemplateRooms] = useState<Array<string>>([]);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    templates,
    request_options: requestOptions,
    options,
  } = useAppSelector((state) => state.requests);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  // Functions
  function selectTemplateToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [templateId] = args;
    const templateData =
      templates.data === null
        ? null
        : templates.data.filter((template) => template.id === templateId)[0];

    const validRooms = [];
    for (let i = 0; i < roomsData.length; i++) {
      const room = roomsData[i];
      const roomRequestOptions = room.request_options.map((x: any) => x.id);
      const templateRequestOptions =
        templateData === null
          ? []
          : templateData.template.map((x: any) => x.id);
      if (
        JSON.stringify(roomRequestOptions) ===
        JSON.stringify(templateRequestOptions)
      ) {
        validRooms.push(room.id);
      }
    }
    setSelectedTemplate(templateData);
    setEditTemplateRooms(validRooms);
    setShowEditModal(true);
  }

  function openCreateTemplateModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function updateRequestOptionForCreateTemplate(requestId: string) {
    const currentRequestOptions = createTemplateForm.template;
    const currentRequestOptionsIds = currentRequestOptions.map(
      (x: any) => x.id,
    );
    if (currentRequestOptionsIds.includes(requestId)) {
      const updatedRequestOptions = currentRequestOptions.filter(
        (x: any) => x.id !== requestId,
      );
      setCreateTemplateForm({
        ...createTemplateForm,
        template: updatedRequestOptions,
      });
    } else {
      const requestOptionToAdd =
        requestOptions.data === null
          ? null
          : requestOptions.data.filter((x: any) => x.id === requestId)[0];
      if (requestOptionToAdd === null || requestOptionToAdd === undefined)
        return;
      const formattedRequestOptionToAdd = {
        id: requestOptionToAdd.id,
        category: requestOptionToAdd.category,
        translation: requestOptionToAdd.translation,
        status: requestOptionToAdd.status,
      };
      const updatedRequestOptions = [
        ...currentRequestOptions,
        formattedRequestOptionToAdd,
      ];
      setCreateTemplateForm({
        ...createTemplateForm,
        template: updatedRequestOptions,
      });
    }
  }

  function updateRequestOptionForUpdateTemplate(requestId: string) {
    const currentRequestOptions =
      selectedTemplate === null ? [] : selectedTemplate.template;
    const currentRequestOptionsIds = currentRequestOptions.map(
      (x: any) => x.id,
    );
    if (currentRequestOptionsIds.includes(requestId)) {
      const updatedRequestOptions = currentRequestOptions.filter(
        (x: any) => x.id !== requestId,
      );
      if (selectedTemplate !== null)
        setSelectedTemplate({
          ...selectedTemplate,
          template: updatedRequestOptions,
        });
    } else {
      const requestOptionToAdd =
        requestOptions.data === null
          ? null
          : requestOptions.data.filter((x: any) => x.id === requestId)[0];
      if (requestOptionToAdd === null || requestOptionToAdd === undefined)
        return;
      const formattedRequestOptionToAdd = {
        id: requestOptionToAdd.id,
        category: requestOptionToAdd.category,
        translation: requestOptionToAdd.translation,
        status: requestOptionToAdd.status,
      };
      const updatedRequestOptions = [
        ...currentRequestOptions,
        formattedRequestOptionToAdd,
      ];
      if (selectedTemplate !== null)
        setSelectedTemplate({
          ...selectedTemplate,
          template: updatedRequestOptions,
        });
    }
  }

  function selectOrUnselectAllRequestOptions(e: any, args: any[]) {
    e.preventDefault();
    const [creating, selectAll] = args;
    if (!creating) {
      if (selectAll) {
        const allAvailableOptions =
          requestOptions.data === null
            ? []
            : requestOptions.data.map((x) => ({
                id: x.id,
                category: x.category,
                translation: x.translation,
                status: x.status,
              }));
        if (selectedTemplate !== null)
          setSelectedTemplate({
            ...selectedTemplate,
            template: allAvailableOptions,
          });
      } else {
        if (selectedTemplate !== null)
          setSelectedTemplate({ ...selectedTemplate, template: [] });
      }
      return;
    }
    if (selectAll) {
      const allAvailableOptions =
        requestOptions.data === null
          ? []
          : requestOptions.data.map((x) => ({
              id: x.id,
              category: x.category,
              translation: x.translation,
              status: x.status,
            }));
      setCreateTemplateForm({
        ...createTemplateForm,
        template: allAvailableOptions,
      });
    } else {
      setCreateTemplateForm({ ...createTemplateForm, template: [] });
    }
  }

  function selectOrUnselectAllRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setEditTemplateRooms(allAvailableRooms);
    } else {
      setEditTemplateRooms([]);
    }
  }

  function updateRoomForTemplate(roomId: string) {
    if (editTemplateRooms.includes(roomId)) {
      const updatedRooms = editTemplateRooms.filter((x) => x !== roomId);
      setEditTemplateRooms(updatedRooms);
    } else {
      const updatedRooms = [...editTemplateRooms, roomId];
      setEditTemplateRooms(updatedRooms);
    }
  }

  function createCardText(templateId: string) {
    if (templates.data === null) return null;
    const templateData = templates.data.filter(
      (template) => template.id === templateId,
    )[0];
    return `${templateData.template.length} ${translateText(
      "requests",
      language,
    )}`;
  }

  async function confirmCreateTemplate(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { name, template } = createTemplateForm;
    if (name.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (template.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_request_options", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    const formattedRequestOptions = template.map((x: any) => x.id);
    const obj = {
      name,
      property: user.property.id,
      options: formattedRequestOptions,
    };
    setShowCreateModal(false);
    const res: any = await dispatch(createRequestOptionTemplate(obj));
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_request_option_template", language),
          "success",
          3000,
        ),
      );
      setCreateTemplateForm({ name: "", template: [] });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditTemplate(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (selectedTemplate === null) {
      dispatch(
        addAlert(
          generateAlertText("missing_request_option_template", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    const { id: templateId, name, template } = selectedTemplate;
    if (template.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_request_options", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    const formattedRequestOptions = template.map((x: any) => x.id);

    // Figure out original rooms associated to template
    const originalTemplateData = templatesData.filter(
      (x) => x.id === templateId,
    )[0];
    const originalTemplateIds = originalTemplateData.template.map((x) => x.id);
    originalTemplateIds.sort();
    const originalRooms = [];
    for (let i = 0; i < roomsData.length; i++) {
      const room = roomsData[i];
      const roomRequestOptions = room.request_options.map((x: any) => x.id);
      roomRequestOptions.sort();
      if (
        JSON.stringify(roomRequestOptions) ===
        JSON.stringify(originalTemplateIds)
      ) {
        originalRooms.push(room.id);
      }
    }
    const roomsToRemove = originalRooms.filter(
      (x) => !editTemplateRooms.includes(x),
    );

    const obj = {
      templateId,
      name,
      property: user.property.id,
      options: formattedRequestOptions,
      roomsApply: editTemplateRooms,
      roomsRemove: roomsToRemove,
    };
    setShowEditModal(false);
    const res: any = await dispatch(updateRequestOptionTemplate(obj));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_request_option_template", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function checkIfRoomChecked(roomId: string) {
    if (selectedTemplate === null) return false;
    return editTemplateRooms.includes(roomId);
  }

  async function confirmDeleteTemplate(e: any, args: any[]) {
    e.preventDefault();
    const [templateId] = args;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (templateId === null || templateId === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_request_option_template", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deleteRequestOptionTemplate({ templateId }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const templatesData = templates.data === null ? [] : templates.data;

  // Create list of all possible request options within property
  const possibleRequestOptions =
    requestOptions.data === null ? [] : requestOptions.data;
  const possibleRequestOptionsHierarchy =
    options.requests === undefined
      ? []
      : Object.keys(options.requests).map((option: any) => {
          let validRequestOptions = possibleRequestOptions.filter(
            (x: any) => x.category === option,
          );
          validRequestOptions.sort((a, b) =>
            a.translation.localeCompare(b.translation),
          );
          return {
            category: option,
            requests: validRequestOptions,
          };
        });

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("rooms_templates_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateTemplateModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_template", language)}
              </Button>
            }
          />
        }
      />

      {/* Create room modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_template", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateTemplate}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_template", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createTemplateForm}
                stateName="name"
                onChange={setCreateTemplateForm}
              />

              {/* Request options */}
              <div>
                <label className="mb-1 block text-left text-sm font-medium text-black">
                  {translateText("request_options", language)}
                </label>
                <p className="text-left text-xs italic text-secondary">
                  {translateText("template_options_subtext", language)}
                </p>
              </div>

              {/* Select/unselect all buttons */}
              <div className="grid grid-cols-2">
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-success hover:bg-success/30 hover:text-success-dark`}
                  onClick={selectOrUnselectAllRequestOptions}
                  onClickArgs={[true, true]}
                >
                  <CheckIcon
                    className={`h-5 w-5 text-success group-hover:text-success-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-success-dark group-hover:text-success-dark">
                    {translateText("select_all", language)}
                  </span>
                </Button>
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-warning hover:bg-warning/30 hover:text-warning-dark`}
                  onClick={selectOrUnselectAllRequestOptions}
                  onClickArgs={[true, false]}
                >
                  <XMarkIcon
                    className={`h-5 w-5 text-warning group-hover:text-warning-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-warning group-hover:text-warning-dark">
                    {translateText("unselect_all", language)}
                  </span>
                </Button>
              </div>
              <div className="grid grid-cols-1 space-y-8">
                {possibleRequestOptionsHierarchy.map((category: any) => {
                  const readableCategory =
                    options.requests[category.category][language];
                  return (
                    <div key={`${category.category}-request-options`}>
                      <div className="text-left text-sm underline">
                        <span>{readableCategory}</span>
                      </div>
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {category.requests.map((request: any) => {
                          return (
                            <Checkbox
                              key={request.id}
                              id={request.id}
                              onChange={updateRequestOptionForCreateTemplate}
                              checked={
                                createTemplateForm.template.filter(
                                  (x: any) => x.id === request.id,
                                ).length > 0
                              }
                              label={request.translation}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit room modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_template", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditTemplate}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`Edit room "${selectedTemplate?.name}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created by */}
              <DisabledTextInput
                id="created_by"
                label={translateText("created_by", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedTemplate === null ? "" : selectedTemplate.created_by
                }
              />

              {/* Updated by */}
              {selectedTemplate !== null &&
                selectedTemplate.updated_by !== null && (
                  <DisabledTextInput
                    id="updated_by"
                    label={translateText("updated_by", language)}
                    className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                    showLabel={true}
                    value={
                      selectedTemplate === null
                        ? ""
                        : selectedTemplate.updated_by
                    }
                  />
                )}

              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedTemplate}
                stateName="name"
                onChange={setSelectedTemplate}
              />

              {/* Request options */}
              <div>
                <label className="mb-1 block text-left text-sm font-medium text-black">
                  {translateText("request_options", language)}
                </label>
                <p className="text-left text-xs italic text-secondary">
                  {translateText("request_options_subtext", language)}
                </p>
              </div>

              {/* Select/unselect all buttons */}
              <div className="grid grid-cols-2">
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-success hover:bg-success/30 hover:text-success-dark`}
                  onClick={selectOrUnselectAllRequestOptions}
                  onClickArgs={[false, true]}
                >
                  <CheckIcon
                    className={`h-5 w-5 text-success group-hover:text-success-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-success-dark group-hover:text-success-dark">
                    {translateText("select_all", language)}
                  </span>
                </Button>
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-warning hover:bg-warning/30 hover:text-warning-dark`}
                  onClick={selectOrUnselectAllRequestOptions}
                  onClickArgs={[false, false]}
                >
                  <XMarkIcon
                    className={`h-5 w-5 text-warning group-hover:text-warning-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-warning group-hover:text-warning-dark">
                    {translateText("unselect_all", language)}
                  </span>
                </Button>
              </div>
              <div className="grid grid-cols-1 space-y-8">
                {possibleRequestOptionsHierarchy.map((category: any) => {
                  const readableCategory =
                    options.requests[category.category][language];
                  return (
                    <div key={`${category.category}-request-options`}>
                      <div className="text-left text-sm underline">
                        <span>{readableCategory}</span>
                      </div>
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {category.requests.map((request: any) => {
                          return (
                            <Checkbox
                              key={request.id}
                              id={request.id}
                              onChange={updateRequestOptionForUpdateTemplate}
                              checked={
                                selectedTemplate === null
                                  ? false
                                  : selectedTemplate.template.filter(
                                      (x: any) => x.id === request.id,
                                    ).length > 0
                              }
                              label={request.translation}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Rooms */}
              <div>
                <label className="mb-1 block text-left text-sm font-medium text-black">
                  {translateText("rooms", language)}
                </label>
                <p className="text-left text-xs italic text-secondary">
                  {translateText("rooms_template_subtext", language)}
                </p>
              </div>

              {/* Select/unselect all buttons */}
              <div className="grid grid-cols-2">
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-success hover:bg-success/30 hover:text-success-dark`}
                  onClick={selectOrUnselectAllRooms}
                  onClickArgs={[true]}
                >
                  <CheckIcon
                    className={`h-5 w-5 text-success group-hover:text-success-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-success-dark group-hover:text-success-dark">
                    {translateText("select_all", language)}
                  </span>
                </Button>
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-warning hover:bg-warning/30 hover:text-warning-dark`}
                  onClick={selectOrUnselectAllRooms}
                  onClickArgs={[false]}
                >
                  <XMarkIcon
                    className={`h-5 w-5 text-warning group-hover:text-warning-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-warning group-hover:text-warning-dark">
                    {translateText("unselect_all", language)}
                  </span>
                </Button>
              </div>
              <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                {roomsData.map((room: any) => (
                  <Checkbox
                    key={room.id}
                    id={room.id}
                    onChange={updateRoomForTemplate}
                    checked={checkIfRoomChecked(room.id)}
                    label={room.name}
                  />
                ))}
              </div>

              {/* Danger zone */}
              {selectedTemplate !== null && (
                <DangerZone
                  objectId={selectedTemplate.id}
                  handler={confirmDeleteTemplate}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {templates.loading || requestOptions.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {templatesData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {templatesData.map((template) => (
                    <a
                      href="#!"
                      key={template.id}
                      onClick={(e) => selectTemplateToEdit(e, [template.id])}
                    >
                      <Card
                        key={template.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {template.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className={`text-sm text-secondary`}>
                            {createCardText(template.id)}
                          </p>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_request_templates", language)}
                subtext={translateText(
                  "no_request_templates_subtext",
                  language,
                )}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
